import { StandardEvents } from '@shopify/events';

const CONFIG_TIMEOUT_MS = 8000;
const TURNSTILE_LOAD_TIMEOUT_MS = 10000;
const REQUEST_TIMEOUT_MS = 15000;
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

async function fetchJsonWithTimeout(input, init = {}, timeoutMs) {
  const controller = new AbortController();
  const upstreamSignal = init.signal;
  const abort = () => controller.abort();
  const timeout = setTimeout(abort, timeoutMs);

  if (upstreamSignal) {
    if (upstreamSignal.aborted) controller.abort();
    else upstreamSignal.addEventListener('abort', abort, { once: true });
  }

  try {
    const response = await fetch(input, { ...init, signal: controller.signal });
    const data = await response.json();
    return { response, data };
  } finally {
    clearTimeout(timeout);
    upstreamSignal?.removeEventListener('abort', abort);
  }
}

function loadTurnstile(timeoutMs = TURNSTILE_LOAD_TIMEOUT_MS) {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (window.slRestockChallenge) return window.slRestockChallenge;

  const loading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    let settled = false;
    let timeout;

    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      script.onload = null;
      script.onerror = null;
      if (error) {
        script.remove();
        reject(error);
      } else {
        resolve(window.turnstile);
      }
    };

    script.src = TURNSTILE_SRC;
    script.async = true;
    script.dataset.slRestockTurnstile = '';
    script.onload = () => finish(window.turnstile ? null : new Error('turnstile_unavailable'));
    script.onerror = () => finish(new Error('turnstile_load_failed'));
    timeout = setTimeout(() => finish(new Error('turnstile_load_timeout')), timeoutMs);
    document.head.append(script);
  });

  const shared = loading.catch((error) => {
    if (window.slRestockChallenge === shared) window.slRestockChallenge = null;
    throw error;
  });
  window.slRestockChallenge = shared;
  return shared;
}

class SlowlinesRestock extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.variants = JSON.parse(this.querySelector('[data-restock-variants]').textContent);
    this.form = this.querySelector('form');
    this.details = this.querySelector('details');
    this.submitButton = this.querySelector('[data-restock-submit]');
    this.feedback = this.querySelector('[data-restock-feedback]');
    this.retryButton = this.querySelector('[data-restock-retry]');
    this.contactFallback = this.querySelector('[data-restock-contact-fallback]');
    this.verificationContainer = this.querySelector('[data-restock-verification]');
    this.details.addEventListener('toggle', () => {
      if (this.details.open) this.querySelector('[data-restock-email]')?.focus({ preventScroll: true });
    }, { signal });
    this.dataset.enhanced = 'true';
    this.querySelector('[data-restock-cancel]')?.addEventListener('click', () => {
      this.details.open = false;
      this.querySelector('[data-restock-email]').value = '';
      this.details.querySelector('summary').focus();
    }, { signal });
    this.form.addEventListener('submit', (event) => {
      if (this.dataset.apiUrl) { event.preventDefault(); this.sendRequest(); return; }
      if (this.selectionBlocked() || this.dataset.unavailable !== 'true') {
        event.preventDefault();
        this.showError();
      }
    }, { signal });
    // Capture before the purchase component can enqueue a stale selection.
    this.closest('.shopify-section')?.addEventListener('submit', (event) => {
      if (!this.selectionBlocked()) return;
      const purchase = event.target.closest?.('product-form-component');
      if (!purchase || (purchase.dataset.productId && purchase.dataset.productId !== this.dataset.productId)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { capture: true, signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, (event) => {
      const picker = event.target.closest?.('variant-picker');
      if (!picker || String(picker.dataset.productId) !== this.dataset.productId) return;
      const generation = (this.generation || 0) + 1;
      this.generation = generation;
      // Keep the confirmed purchase surface in place until the server confirms the new size.
      this.dataset.pending = 'true';
      this.dataset.selectionError = 'false';
      this.querySelector('.sl-restock__error').hidden = true;
      this.setPurchaseBlocked(true);
      if (this.submitButton) this.submitButton.disabled = true;
      if (!event.promise) { this.showError(); return; }
      event.promise.then(({ detail }) => {
        if (this.generation !== generation || !this.isConnected) return;
        const next = this.variants.find(v => String(v.id) === String(detail?.resource?.id));
        if (next && typeof detail.resource.available === 'boolean') {
          next.available = detail.resource.available;
          this.render(next);
        } else this.showError();
      }).catch(() => {
        if (this.generation === generation && this.isConnected) this.showError();
      });
    }, { signal });
    if (this.dataset.apiUrl) {
      this.details.addEventListener('toggle', () => { if (this.details.open) this.prepareVerification(); }, { signal });
      this.retryButton?.addEventListener('click', () => this.retryVerification(), { signal });
      this.watchVerificationSize(signal);
    }
    this.render(this.variants.find(v => String(v.id) === this.dataset.variantId), true);
    this.setContactFallback(false);
  }

  selectionBlocked() {
    return this.dataset.pending === 'true' || this.dataset.selectionError === 'true';
  }

  setPurchaseBlocked(blocked) {
    // Keep the live loading status outside the busy form so it can be announced immediately.
    this.form.setAttribute('aria-busy', String(this.dataset.pending === 'true'));
    const loading = this.querySelector('.sl-restock__loading');
    if (loading) loading.textContent = this.dataset.pending === 'true' ? this.dataset.loadingLabel : '';
    this.form.inert = blocked;
    this.closest('.shopify-section')?.querySelectorAll('product-form-component, sticky-add-to-cart').forEach(element => {
      if (element.dataset.productId && element.dataset.productId !== this.dataset.productId) return;
      element.inert = blocked;
      element.setAttribute('aria-disabled', String(blocked));
      element.setAttribute('aria-busy', String(this.dataset.pending === 'true'));
    });
  }

  showError() {
    this.dataset.pending = 'false';
    this.dataset.selectionError = 'true';
    this.setPurchaseBlocked(true);
    this.querySelector('.sl-restock__error').hidden = false;
    if (this.submitButton) this.submitButton.disabled = true;
  }

  render(variant, initial = false, pending = false) {
    if (!variant) { this.showError(); return; }
    this.dataset.pending = String(pending);
    this.dataset.selectionError = 'false';
    this.setPurchaseBlocked(pending);
    this.querySelector('.sl-restock__error').hidden = true;
    const unavailable = !variant.available;
    this.dataset.unavailable = String(unavailable);
    this.dataset.variantId = String(variant.id);
    if (!initial) this.querySelectorAll('[data-restock-feedback]').forEach(el => { el.hidden = true; });
    this.querySelector('.sl-restock__panel').hidden = !unavailable && !(initial && this.querySelector('[data-restock-feedback]:not([hidden])'));
    const size = this.querySelector('[data-restock-size]');
    if (size) size.textContent = variant.title === 'Default Title' ? '' : `${variant.title} · `;
    this.closest('.shopify-section')?.querySelectorAll('variant-picker input[data-variant-id]').forEach(input => {
      const item = this.variants.find(v => String(v.id) === input.dataset.variantId);
      if (item && !item.available) {
        input.removeAttribute('aria-disabled');
        input.setAttribute('aria-label', this.dataset.variantLabel.replace('__VARIANT__', item.title));
      }
    });
    if (this.submitButton) this.submitButton.disabled = pending || !unavailable;
    const title = this.querySelector('[data-restock-variant-title]');
    const id = this.querySelector('[data-restock-variant-id]');
    const link = this.querySelector('[data-restock-product-link]');
    const returnTo = this.form.querySelector('[name="return_to"]');
    if (title) title.value = variant.title;
    if (id) id.value = String(variant.id);
    const path = `${this.dataset.productUrl}?variant=${variant.id}`;
    if (link) link.value = new URL(path, location.origin).href;
    if (returnTo) returnTo.value = `${path}#${this.dataset.requestId}`;
    if (!initial && this.details) this.details.open = false;
  }

  async prepareVerification() {
    if (this.widgetId !== undefined || this.loadingVerification) return;
    this.loadingVerification = true;
    try {
      const endpoint = new URL(this.dataset.apiUrl);
      if (endpoint.protocol !== 'https:') throw new Error();
      const { response: configResponse, data: config } = await fetchJsonWithTimeout(
        endpoint.origin + '/api/config',
        { signal: this.abort.signal },
        CONFIG_TIMEOUT_MS
      );
      if (!configResponse.ok) throw new Error();
      if (!config.ready || !config.siteKey) throw new Error();
      await loadTurnstile();
      if (!this.isConnected) return;
      this.challengeSize = this.getChallengeSize();
      this.widgetFailed = false;
      this.retryButton.hidden = true;
      this.setContactFallback(false);
      this.widgetId = window.turnstile.render(this.verificationContainer, {
        sitekey: config.siteKey,
        action: 'restock',
        theme: 'light',
        appearance: 'interaction-only',
        size: this.challengeSize,
        callback: (token) => {
          this.verificationToken = token;
          this.retryButton.hidden = true;
          this.setContactFallback(false);
          if (this.feedback.dataset.verificationError === 'true') this.hideFeedback();
        },
        'expired-callback': () => { this.verificationToken = ''; },
        'timeout-callback': () => this.verificationFailed('Verification timed out. Please try again.'),
        'error-callback': () => this.verificationFailed('Verification failed. Please try again.')
      });
    } catch {
      this.clearVerificationWidget();
      this.showRecovery('Restock requests are temporarily unavailable. Try again or contact us.');
    } finally {
      this.loadingVerification = false;
    }
  }

  getChallengeSize() {
    const width = this.verificationContainer?.getBoundingClientRect().width || this.clientWidth;
    return width < 300 ? 'compact' : 'normal';
  }

  watchVerificationSize(signal) {
    const schedule = () => {
      if (!this.details.open || this.widgetId === undefined || this.resizeFrame) return;
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = 0;
        if (this.getChallengeSize() === this.challengeSize) return;
        this.clearVerificationWidget();
        this.prepareVerification();
      });
    };

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(schedule);
      this.resizeObserver.observe(this.verificationContainer);
    } else {
      window.addEventListener('resize', schedule, { signal });
      window.addEventListener('orientationchange', schedule, { signal });
    }
  }

  retryVerification() {
    this.widgetFailed = false;
    this.clearVerificationWidget();
    this.retryButton.hidden = true;
    this.feedback.hidden = false;
    this.feedback.dataset.verificationError = 'true';
    this.feedback.textContent = 'Loading verification…';
    this.prepareVerification();
  }

  verificationFailed(message) {
    this.verificationToken = '';
    this.widgetFailed = true;
    this.showRecovery(message);
    setTimeout(() => {
      if (this.widgetFailed) this.clearVerificationWidget();
    }, 0);
  }

  showRecovery(message) {
    this.feedback.hidden = false;
    this.feedback.dataset.verificationError = 'true';
    this.feedback.textContent = message;
    this.retryButton.hidden = false;
    this.setContactFallback(true);
  }

  hideFeedback() {
    this.feedback.hidden = true;
    this.feedback.textContent = '';
    delete this.feedback.dataset.verificationError;
  }

  setContactFallback(visible) {
    if (this.contactFallback) this.contactFallback.hidden = !visible;
  }

  clearVerificationWidget() {
    this.widgetFailed = false;
    this.verificationToken = '';
    if (this.widgetId !== undefined && window.turnstile) {
      try { window.turnstile.remove(this.widgetId); } catch {}
    }
    this.widgetId = undefined;
    this.challengeSize = undefined;
  }

  async sendRequest() {
    const feedback = this.querySelector('[data-restock-feedback]');
    if (this.sending || this.selectionBlocked() || this.dataset.unavailable !== 'true') return;
    if (!this.form.reportValidity()) return;
    feedback.hidden = false;
    if (!this.verificationToken) { feedback.textContent = 'Please wait for verification and try again.'; await this.prepareVerification(); return; }
    const selectedId = this.dataset.variantId;
    const generation = this.generation || 0;
    this.sending = true; this.submitButton.disabled = true; feedback.textContent = 'Saving your request…';
    const controls = [...(this.closest('.shopify-section')?.querySelectorAll('variant-picker input, variant-picker select, variant-picker button') || [])];
    const priorDisabled = controls.map(control => control.disabled);
    controls.forEach(control => { control.disabled = true; });
    const close = this.querySelector('[data-restock-cancel]');
    if (close) close.disabled = true;
    try {
      const { response, data: result } = await fetchJsonWithTimeout(this.dataset.apiUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: this.abort.signal,
        body: JSON.stringify({ email: this.querySelector('[data-restock-email]').value,
          handle: this.dataset.productHandle, variantId: selectedId, consent: this.querySelector('[data-restock-consent]').checked,
          turnstileToken: this.verificationToken, website: this.querySelector('[name="website"]').value })
      }, REQUEST_TIMEOUT_MS);
      if (!this.isConnected || this.dataset.variantId !== selectedId || (this.generation || 0) !== generation) return;
      feedback.replaceChildren(document.createTextNode(response.ok ? result.message : (result.error || 'Could not save your request. Please try again.')));
      if (response.ok) {
        this.querySelector('[data-restock-email]').value = '';
        if (result.cancelUrl) {
          const linkUrl = new URL(result.cancelUrl);
          if (linkUrl.origin === new URL(this.dataset.apiUrl).origin && linkUrl.pathname === '/cancel') {
            const link = document.createElement('a'); link.href = linkUrl.href; link.textContent = 'Save this link to cancel your request.'; link.rel = 'noreferrer';
            feedback.append(document.createElement('br'), link);
          }
        }
      }
    } catch {
      if (this.dataset.variantId === selectedId) {
        feedback.textContent = 'Could not confirm your request. Try again with the same email; it will not create a duplicate.';
        this.setContactFallback(true);
      }
    } finally {
      controls.forEach((control, index) => { control.disabled = priorDisabled[index]; });
      if (close) close.disabled = false;
      this.sending = false; this.verificationToken = '';
      if (this.widgetId !== undefined && window.turnstile) {
        try { window.turnstile.reset(this.widgetId); } catch { this.clearVerificationWidget(); }
      }
      this.submitButton.disabled = this.dataset.unavailable !== 'true' || this.selectionBlocked();
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.resizeObserver?.disconnect();
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    this.clearVerificationWidget();
  }
}

if (!customElements.get('sl-restock')) customElements.define('sl-restock', SlowlinesRestock);
